import { useEffect, useRef } from 'react';
import { useAuthContext } from '/src/hooks';

export function useTrialUpgradeTracking() {
  const { currentUser } = useAuthContext();
  const hasTrackedRef = useRef(false);
  const pollingRef = useRef(null);
  const pollingCountRef = useRef(0);

  useEffect(() => {
    // Check if returning from billing
    const isReturningFromBilling = sessionStorage.getItem('ce_upgrade_flow');
    if (!isReturningFromBilling) return;

    // Get stored pre-upgrade state
    const storedState = JSON.parse(sessionStorage.getItem('ce_pre_upgrade_state') || '{}');

    // Check if we have subscription data
    if (!currentUser?.subscriptions) {
      // Start polling if we don't have data yet
      startPolling();
      return;
    }

    // Determine what type of plan they had before
    const wasFreeBefore = storedState.subscriptions?.every(
      (sub) =>
        sub.paymentPlan?.isFreemium || sub.paymentPlan?.isFree || (!sub.paymentPlan?.isTrial && !sub.paymentPlan?.name),
    );

    const wasTrialBefore = storedState.subscriptions?.some((sub) => sub.paymentPlan?.isTrial === true);

    // Check current subscription status
    const currentSnapshots = currentUser?.subscriptions?.snapshots;
    const currentRecordings = currentUser?.subscriptions?.recordings;

    const isTrialNow =
      currentSnapshots?.paymentPlan?.isTrial === true || currentRecordings?.paymentPlan?.isTrial === true;

    const isPaidNow =
      (currentSnapshots &&
        !currentSnapshots.paymentPlan?.isFree &&
        !currentSnapshots.paymentPlan?.isFreemium &&
        !currentSnapshots.paymentPlan?.isTrial) ||
      (currentRecordings &&
        !currentRecordings.paymentPlan?.isFree &&
        !currentRecordings.paymentPlan?.isFreemium &&
        !currentRecordings.paymentPlan?.isTrial);

    // Detect upgrade from free/freemium to trial
    if (wasFreeBefore && isTrialNow && !hasTrackedRef.current) {
      // Deduplicate with sessionStorage flag (includes userId and timestamp for uniqueness)
      const trackingKey = `ce_trial_tracked_${currentUser?.userId}_${Math.floor(Date.now() / 10000)}`;

      // Check if we already tracked this in another tab/window
      const alreadyTracked = sessionStorage.getItem(trackingKey);

      if (!alreadyTracked) {
        fireTrialStartedEvent(currentUser);
        sessionStorage.setItem(trackingKey, 'true');
        hasTrackedRef.current = true;

        // Cleanup after successful tracking
        cleanupUpgradeFlow();
      }
    }
    // Detect upgrade to paid plan
    else if ((wasFreeBefore || wasTrialBefore) && isPaidNow && !hasTrackedRef.current) {
      // Deduplicate with sessionStorage flag (includes userId and timestamp for uniqueness)
      const timestampKey = Math.floor(Date.now() / 10000);
      const trialTrackingKey = `ce_trial_tracked_${currentUser?.userId}_${timestampKey}`;
      const paidTrackingKey = `ce_paid_tracked_${currentUser?.userId}_${timestampKey}`;

      // Check if we already tracked this in another tab/window
      const alreadyTracked = sessionStorage.getItem(paidTrackingKey);

      if (!alreadyTracked) {
        // If upgrading from free/freemium directly to paid, fire trial event first (for funnel tracking)
        if (wasFreeBefore && !wasTrialBefore) {
          // Check if trial event wasn't already fired
          if (!sessionStorage.getItem(trialTrackingKey)) {
            fireTrialStartedEvent(currentUser, true); // Pass flag to indicate this is for a paid upgrade
            sessionStorage.setItem(trialTrackingKey, 'true');
          }
        }

        // Always fire the paid upgrade event
        firePaidUpgradeEvent(currentUser);
        sessionStorage.setItem(paidTrackingKey, 'true');
        hasTrackedRef.current = true;

        // Cleanup after successful tracking
        cleanupUpgradeFlow();
      }
    } else if (isTrialNow || isPaidNow) {
      // User already has trial or paid plan, cleanup
      cleanupUpgradeFlow();
    } else if (isReturningFromBilling && !isTrialNow && !isPaidNow) {
      // Still returning but no trial or paid yet, might be a race condition
      startPolling();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const startPolling = () => {
    if (pollingRef.current) return;

    pollingRef.current = setInterval(() => {
      pollingCountRef.current++;

      // Stop polling after 20 seconds (10 attempts at 2 second intervals)
      if (pollingCountRef.current > 10) {
        stopPolling();
        cleanupUpgradeFlow();
      }
    }, 2000);
  };

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
      pollingCountRef.current = 0;
    }
  };

  const cleanupUpgradeFlow = () => {
    sessionStorage.removeItem('ce_upgrade_flow');
    sessionStorage.removeItem('ce_pre_upgrade_state');
    sessionStorage.removeItem('ce_upgrade_timestamp');
    stopPolling();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  // Also check for URL parameters as a fallback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const trialStarted = urlParams.get('trial_started');
    const paymentSuccess = urlParams.get('payment_success');

    if ((trialStarted === 'true' || paymentSuccess === 'true') && currentUser?.subscriptions) {
      const currentSnapshots = currentUser?.subscriptions?.snapshots;
      const currentRecordings = currentUser?.subscriptions?.recordings;

      const isTrialNow =
        currentSnapshots?.paymentPlan?.isTrial === true || currentRecordings?.paymentPlan?.isTrial === true;

      const isPaidNow =
        (currentSnapshots &&
          !currentSnapshots.paymentPlan?.isFree &&
          !currentSnapshots.paymentPlan?.isFreemium &&
          !currentSnapshots.paymentPlan?.isTrial) ||
        (currentRecordings &&
          !currentRecordings.paymentPlan?.isFree &&
          !currentRecordings.paymentPlan?.isFreemium &&
          !currentRecordings.paymentPlan?.isTrial);

      const timestampKey = Math.floor(Date.now() / 10000);
      const trialTrackingKey = `ce_trial_tracked_${currentUser?.userId}_${timestampKey}`;
      const paidTrackingKey = `ce_paid_tracked_${currentUser?.userId}_${timestampKey}`;

      if (isTrialNow && !hasTrackedRef.current) {
        if (!sessionStorage.getItem(trialTrackingKey)) {
          fireTrialStartedEvent(currentUser);
          sessionStorage.setItem(trialTrackingKey, 'true');
          hasTrackedRef.current = true;
        }
      } else if (isPaidNow && !hasTrackedRef.current) {
        if (!sessionStorage.getItem(paidTrackingKey)) {
          // For URL parameter fallback, we assume they might be coming from free/freemium
          // Fire trial event first if not already tracked (for funnel tracking)
          if (!sessionStorage.getItem(trialTrackingKey)) {
            fireTrialStartedEvent(currentUser, true); // Pass flag to indicate this is for a paid upgrade
            sessionStorage.setItem(trialTrackingKey, 'true');
          }

          firePaidUpgradeEvent(currentUser);
          sessionStorage.setItem(paidTrackingKey, 'true');
          hasTrackedRef.current = true;
        }
      }

      if ((isTrialNow || isPaidNow) && hasTrackedRef.current) {
        // Clean up URL
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('trial_started');
        newUrl.searchParams.delete('payment_success');
        window.history.replaceState({}, document.title, newUrl.toString());
      }
    }
  }, [currentUser]);
}

function calculateMonthlyAmount(subscription) {
  if (!subscription?.amount) return 0;

  // Handle different interval types
  const intervalType = subscription.intervalType?.toLowerCase();
  const interval = subscription.interval || 1;

  if (intervalType === 'year' || intervalType === 'yearly') {
    return Math.round((subscription.amount / (interval * 12)) * 100) / 100; // Divide yearly by 12, round to 2 decimals
  } else if (intervalType === 'month' || intervalType === 'monthly') {
    return subscription.amount / interval; // Monthly amount divided by interval
  }

  // Default to treating as monthly if intervalType is not set
  return subscription.amount;
}

function fireTrialStartedEvent(user, isForPaidUpgrade = false) {
  // Find the trial subscription, or use the paid subscription if this is for a paid upgrade
  let trialSub = null;

  if (user?.subscriptions?.snapshots?.paymentPlan?.isTrial) {
    trialSub = user.subscriptions.snapshots;
  } else if (user?.subscriptions?.recordings?.paymentPlan?.isTrial) {
    trialSub = user.subscriptions.recordings;
  } else if (isForPaidUpgrade) {
    // For direct-to-paid upgrades, use the paid subscription data
    trialSub = user?.subscriptions?.snapshots || user?.subscriptions?.recordings;
  }

  if (!trialSub) return;

  // Ensure dataLayer exists
  window.dataLayer = window.dataLayer || [];

  // Calculate monthly amount
  const monthlyAmount = calculateMonthlyAmount(trialSub);

  // Push event to GTM dataLayer
  const gtmEvent = {
    event: 'Start Self-Serve Trial',
    event_category: 'Subscription',
    event_action: 'Trial Started',
    event_label: 'Free to Trial Upgrade',
    user_id: user?.userId,
    guest_id: user?.guestId,
    plan_name: trialSub?.paymentPlan?.name,
    plan_type: trialSub?.paymentPlan?.planType,
    plan_group: trialSub?.paymentPlan?.planGroup,
    upgrade_source: 'shell',
    monthlyAmount: monthlyAmount,
    timestamp: new Date().toISOString(),
  };

  window.dataLayer.push(gtmEvent);

  // Also track via Google Analytics if available
  if (window.gtag) {
    window.gtag('event', 'Start Self-Serve Trial', {
      event_category: 'Subscription',
      event_action: 'Trial Started',
      event_label: 'Free to Trial Upgrade',
      user_id: user?.userId,
      value: monthlyAmount,
    });
  }

  // Log for debugging in development
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log('🎯 GTM Event: Start Self-Serve Trial', gtmEvent);
    // eslint-disable-next-line no-console
    console.log('📊 DataLayer after push:', window.dataLayer);
  }
}

function firePaidUpgradeEvent(user) {
  // Find the paid subscription (prioritize non-free, non-trial subscriptions)
  let paidSub = null;

  const snapshots = user?.subscriptions?.snapshots;
  const recordings = user?.subscriptions?.recordings;

  if (
    snapshots &&
    !snapshots.paymentPlan?.isFree &&
    !snapshots.paymentPlan?.isFreemium &&
    !snapshots.paymentPlan?.isTrial
  ) {
    paidSub = snapshots;
  } else if (
    recordings &&
    !recordings.paymentPlan?.isFree &&
    !recordings.paymentPlan?.isFreemium &&
    !recordings.paymentPlan?.isTrial
  ) {
    paidSub = recordings;
  }

  if (!paidSub) return;

  // Ensure dataLayer exists
  window.dataLayer = window.dataLayer || [];

  // Calculate monthly amount
  const monthlyAmount = calculateMonthlyAmount(paidSub);

  // Push event to GTM dataLayer
  const gtmEvent = {
    event: 'Upgrade to Paid',
    event_category: 'Subscription',
    event_action: 'Upgraded to Paid',
    event_label: 'Paid Plan Upgrade',
    user_id: user?.userId,
    guest_id: user?.guestId,
    plan_name: paidSub?.paymentPlan?.name,
    plan_type: paidSub?.paymentPlan?.planType,
    plan_group: paidSub?.paymentPlan?.planGroup,
    upgrade_source: 'shell',
    monthlyAmount: monthlyAmount,
    timestamp: new Date().toISOString(),
  };

  window.dataLayer.push(gtmEvent);

  // Also track via Google Analytics if available
  if (window.gtag) {
    window.gtag('event', 'Upgrade to Paid', {
      event_category: 'Subscription',
      event_action: 'Upgraded to Paid',
      event_label: 'Paid Plan Upgrade',
      user_id: user?.userId,
      value: monthlyAmount,
    });
  }

  // Log for debugging in development
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log('💰 GTM Event: Upgrade to Paid', gtmEvent);
    // eslint-disable-next-line no-console
    console.log('📊 DataLayer after push:', window.dataLayer);
  }
}
